<script>
	const _ = require('underscore');
	const fuzzysort = require('fuzzysort');

	module.exports = {
		props: {
			value: {
				default: null
			},
			searchDomain: {
				default: null
			},
			searchKey: {
				default: 'name'
			},
			placeholder: {
				default: 'Search'
			},
			pattern: {
				default: '^$|.*\\S.*'
			},
			highlightResult: {
				default: true
			},
			minimumCharactersForSuggestions: {
				default: 3
			},
			titleField: {
				default: 'name'
			},
			titleFormatter: {
				default() {}
			},
			categoryField: {
				default: 'parentName'
			},
			categoryFormatter: {
				default() {}
			},
			showKeywordOption: {
				default: true
			},
			onSuggestionClick: {
				default() {}
			},
			maxlength: {
				default: undefined
			},
			showSuggestionsOnFocus: {
				default: false
			},
			autoSuggest: {
				default: false
			}
		},
		components: {
			'validated-input': require('components/validated-input.vue').default
		},
		data() {
			return {
				focusing: false,
				mouseover: false,
				keyboardIndex: null
			};
		},
		computed: {
			validSearch() {
				let textInput = this.$refs.textInput;
				let value = this.value;
				let minimumCharactersForSuggestions = this.minimumCharactersForSuggestions;

				return !textInput || !textInput.checkValidity() || !value || !(value.length >= minimumCharactersForSuggestions);
			},
			showSuggestions() {
				return this.focusing || this.mouseover;
			},
			suggestions() {
				const value = this.value;
				const searchDomain = this.searchDomain;
				const searchKey = this.searchKey;
				const showKeywordOption = this.showKeywordOption;
				const highlightResult = this.highlightResult;
				const titleField = this.titleField;
				const titleFormatter = this.titleFormatter;
				const categoryField = this.categoryField;
				const categoryFormatter = this.categoryFormatter;
				const categoryClassFormatter = this.categoryClassFormatter;
				const validSearch = this.validSearch;

				// the lower the number,
				// the fuzzier the matches
				let options = {
					threshold: 0.325
				};
				let suggestions = [];
				let results = null;

				// Only search if input field is valid
				if (validSearch) {
					return suggestions;
				}

				if (searchKey) {
					options = _.assign(options, {
						key: searchKey
					});
				}

				if (value && searchDomain && searchDomain.length) {
					if (showKeywordOption) {
						suggestions.unshift({
							id: 0,
							text: value,
							category: 'Keyword',
							categoryClass: categoryClassFormatter('Keyword'),
							score: 0.85,
							obj: {
								text: value,
								keyword: true
							}
						});
					}

					results = fuzzysort.go(value, searchDomain, options);

					if (results && results.length) {
						suggestions = suggestions.concat(
							results.map(result => {
								const {
									obj,
									obj: { id },
									score
								} = result;
								const category = categoryFormatter ? categoryFormatter(obj) : obj[categoryField];
								const categoryClass = categoryClassFormatter(category);
								let text = titleFormatter ? titleFormatter(obj) : obj[titleField];

								if (highlightResult) {
									text = result.highlight();
								}
								return { id, text, category, categoryClass, obj, score };
							})
						);

						if (this.autoSuggest) {
							suggestions.sort((a, b) => (a.score < b.score ? 1 : -1));
						}
					}
				}

				this.keyboardIndex = null;

				return suggestions;
			}
		},
		methods: {
			onResultClick(obj, text) {
				this.mouseover = false;
				this.keyboardIndex = null;
				this.onSuggestionClick(obj, text);
			},
			onKeyDown(event) {
				let key = event.key;
				let keyboardIndex = this.keyboardIndex;
				let suggestions = this.suggestions;
				if (event && event.key && suggestions && suggestions.length) {
					if (key === 'ArrowDown' || key === 'Down') {
						event.preventDefault();
						if (typeof keyboardIndex === 'number') {
							keyboardIndex++;
						} else {
							keyboardIndex = 0;
						}
					} else if (key === 'ArrowUp' || key === 'Up') {
						event.preventDefault();
						if (typeof keyboardIndex === 'number') {
							keyboardIndex--;
						} else {
							keyboardIndex = 0;
						}
					} else if (key === 'Enter' && typeof keyboardIndex === 'number' && suggestions[keyboardIndex]) {
						event.preventDefault();
						this.onResultClick(suggestions[keyboardIndex].obj);
					} else if (key === 'Enter' && this.autoSuggest && suggestions.length > 1) {
						event.preventDefault();
						let suggestion = suggestions[0];
						let keyword = this.value;
						if (suggestion.score === 0 || suggestion.obj.keyword) {
							keyword = undefined;
						}
						this.onResultClick(suggestions[0].obj, keyword);
					}

					if (typeof keyboardIndex === 'number') {
						this.keyboardIndex = Math.max(Math.min(keyboardIndex, suggestions.length - 1), 0);
					}
				}
			},
			categoryClassFormatter(category) {
				const classMap = new Map([
					['Keyword', 'keyword'],
					['Topic', 'topic'],
					['Product', 'product'],
					['Activity Type', 'activity'],
					['Inferring Question Set', 'inferring-qs'],
					['Monitoring Question Set', 'monitoring-qs'],
					['Text Structure Question Set', 'text-structure-qs'],
					['Article-A-Day Vocabulary', 'aad'],
					['Science', 'science'],
					['Social Studies', 'social-studies'],
					['Arts', 'arts'],
					['Social & Emotional Learning', 'sel'],
					['Holidays & Events', 'holidays'],
					['Featured Partners', 'featured-partners'],
					['Book Title', 'book'],
					['Fiction Series', 'series'],
					['Text Type', 'text-type']
				]);
				return classMap.get(category);
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'config/variables';
	@import 'mixins/flexbox';

	.search-bar-with-suggestions {
		.suggestions {
			position: absolute;
			width: 100%;
			z-index: 10;
			background-color: $c-white;
			padding: $size-small;
			border: 1px solid $c-grey;
			box-shadow: 0px 3px 5px $c-black;

			.suggestion {
				@include flexbox();
				position: relative;
				padding: $size-small;
				border: 1px solid transparent;

				&:not(:last-child) {
					border-bottom: 1px solid $c-grey;
				}

				&:hover {
					cursor: pointer; // border-bottom-color: $c-blue;

					.suggestion-text {
						color: $c-blue;
					}
				}

				.suggestion-text {
					@include flex(1);
					text-overflow: ellipsis;
					overflow: hidden;

					&.active {
						color: $c-blue;
					}
				}
			}
		}

		.suggestion-category {
			border-radius: 3px;
			padding: 0 0.75rem;
			color: $c-omni-category-text;

			&.keyword {
				background-color: $c-omni-category-keyword-bg;
			}

			&.topic {
				background-color: $c-omni-category-topic-bg;
			}

			&.product {
				background-color: $c-omni-category-product-bg;
			}

			&.activity {
				background-color: $c-omni-category-activity-bg;
			}

			&.inferring-qs {
				background-color: $c-omni-category-inferring-qs-bg;
			}

			&.monitoring-qs {
				background-color: $c-omni-category-monitoring-qs-bg;
			}

			&.text-structure-qs {
				background-color: $c-omni-category-text-structure-qs-bg;
			}

			&.aad {
				background-color: $c-omni-category-aad-bg;
			}

			&.science {
				background-color: $c-omni-category-science-bg;
			}

			&.social-studies {
				background-color: $c-omni-category-social-studies-bg;
			}

			&.arts {
				background-color: $c-omni-category-arts-bg;
			}

			&.sel {
				background-color: $c-omni-category-sel-bg;
			}

			&.holidays {
				background-color: $c-omni-category-holidays-bg;
			}

			&.featured-partners {
				background-color: $c-omni-category-featured-partners-bg;
			}

			&.book {
				background-color: $c-omni-category-book-bg;
			}

			&.series {
				background-color: $c-omni-category-series-bg;
			}

			&.text-type {
				background-color: $c-omni-category-text-type-bg;
			}
		}
	}
</style>
<template>
	<div class="search-bar-with-suggestions">
		<validated-input
			ref="textInput"
			autocomplete="off"
			class="-rounded"
			type="search"
			v-model="value"
			name="q"
			:pattern="pattern"
			:placeholder="placeholder"
			:maxlength="maxlength"
			@keydown.native="onKeyDown"
			@focus.native="focusing = true"
			@blur.native="focusing = false">
		</validated-input>
		<div
			v-if="showSuggestions && suggestions.length"
			class="suggestions padding-small"
			@mouseover="mouseover = true"
			@mouseout="mouseover = false">
			<div
				v-for="(suggestion, suggestionIndex) in suggestions"
				class="suggestion padding-small"
				:key="suggestion.id"
				:data-node-id="suggestion.id"
				@click="onResultClick(suggestion.obj)">
				<div
					class="suggestion-text"
					:class="{
						active: keyboardIndex === suggestionIndex
					}">
					<b v-if="suggestion.category === 'Keyword'">{{ suggestion.text }}</b>
					<span
						v-else
						v-html="suggestion.text"></span>
				</div>
				<div
					class="suggestion-category"
					:class="suggestion.categoryClass">
					{{ suggestion.category }}
				</div>
			</div>
		</div>
	</div>
</template>
